import { getLanguage as getInterfaceLanguage } from '@/utils/i18n';
import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate as useDomNavigate } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { useCustomAuth } from "@/contexts/CustomAuthContext";
import { authApi } from '@/services/authService';
import {
  ChevronLeft, ChevronRight, Clock, Download,
  Landmark, ArrowUpRight, CheckCircle2, AlertCircle, HelpCircle, Info, X
} from "lucide-react";
import { toast } from "sonner";

import { Header } from '@/components/layout/header/Header';

type STATUS = 'PROCESSANDO' | 'CONCLUIDO' | 'FALHOU' | 'PAGO' | 'SOLICITADO' | 'DEPOSITADO' | 'DISPONIVEL'
interface StatementItem {
  id_transacao: string;
  tipo: 'transfer' | 'payout';
  descricao: string;
  valor: number;
  moeda: string;
  status: STATUS;
  data_solicitacao: string;
  data_credito_banco: string;
  timestamp_ordenacao: number;
  ValotTotal?: number;
  totalTaxas?: number;
  document?: string;
}

type SortField = 'descricao' | 'status' | 'date' | 'valor';
type SortDirection = 'ASC' | 'DESC';

export default function Payouts() {
  useLanguage();
  const { user } = useCustomAuth();
  const navigate = useDomNavigate();

  const [extrato, setExtrato] = useState<StatementItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [payoutLoading, setPayoutLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [taxas, setTaxas] = useState(0);

  // Estado para armazenar a URL do comprovante ativo no modal de visualização
  const [proofModalUrl, setProofModalUrl] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [saldoDisponivelCalculado, setSaldoDisponivelCalculado] = useState(0.0);
  const [saldoSolicitado, setSaldoSolicitado] = useState(0.0);
  const [saldoTotaGeralPago, setSaldoTotaGeralPago] = useState(0.0);
  const [orderBy, setOrderBy] = useState<SortField>('date');
  const [orderDirection, setOrderDirection] = useState<SortDirection>('DESC');
  const limit = 10;

  useEffect(() => {
    if (user && user.nivel === 'customer') {
      toast.error(translateInterfaceText("Área restrita a parceiros e gestores."));
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const fetchData = useCallback(async () => {
    if (!user || user.nivel === 'customer') return;
    try {
      setLoading(true);

      const response = await authApi.get('/stripe/afiliate/finance/user/payouts/history');

      if (response.data.success) {
        setSaldoDisponivelCalculado(response.data.saldo_disponivel)
        setSaldoSolicitado(response.data.saldo_pendente)
        setSaldoTotaGeralPago(response.data.total_geral_pago)
        const backendPayouts = response.data.payouts || [];
        let statusMapeado: STATUS = 'PROCESSANDO';
        const formattedExtrato: StatementItem[] = backendPayouts.map((item: any) => {
          const rawCreated = item.created;
          const dateObj = rawCreated ? new Date(rawCreated * 1000) : new Date();
          const dataFormatada = dateObj.toLocaleDateString(getInterfaceLanguage());

          const currentStatus = String(item.status || '').toLowerCase();

          if (['concluido'].includes(currentStatus)) {
            statusMapeado = 'CONCLUIDO';
          }
          if (['available'].includes(currentStatus)) {
            statusMapeado = 'DISPONIVEL';
          }
          if (['paid', 'success'].includes(currentStatus)) {
            statusMapeado = 'PAGO';
          }
          if (['pending'].includes(currentStatus)) {
            statusMapeado = 'SOLICITADO';
          }
          if (['failed', 'falhou', 'canceled'].includes(currentStatus)) {
            statusMapeado = 'FALHOU';
          }
          const isManualPayout = item.type === 'payout';
          return {
            id_transacao: item.id || Math.random().toString(),
            tipo: isManualPayout ? 'payout' : 'transfer',
            descricao: item.description || (isManualPayout ? "Saque Solicitado" : translateInterfaceText("Comissão por Indicação")),
            valor: Number(item.amount || 0),
            moeda: item.currency || 'brl',
            status: statusMapeado,
            data_solicitacao: dataFormatada,
            data_credito_banco: statusMapeado == 'DISPONIVEL' ? dataFormatada : 'Em processamento',
            ValotTotal: item.totalAmount || undefined,
            totalTaxas: item.totalTaxe || undefined,
            timestamp_ordenacao: dateObj.getTime(),
            document: item.document || undefined
          };
        });
        setExtrato(formattedExtrato);

        const totalDeTaxas = formattedExtrato.reduce((total, item: any) => {
          if (item.tipo === 'payout') {
            return total + (item?.totalTaxas || 0);
          }
          return total;
        }, 0);

        setTaxas(Number(totalDeTaxas));
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message || translateInterfaceText("Erro ao carregar seu extrato de comissões."));
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRequestPayout = async () => {
    try {
      setPayoutLoading(true);
      setIsModalOpen(false);

      const response = await authApi.post('/stripe/afiliate/finance/user/request-manual-payout');

      if (response.data.success) {
        toast.success(response.data.message || translateInterfaceText("Saque solicitado com sucesso!"));
        await fetchData();
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message || translateInterfaceText("Não foi possível processar o saque."));
    } finally {
      setPayoutLoading(false);
    }
  };

  const handleSort = (column: SortField) => {
    const isSame = orderBy === column;
    const direction = isSame && orderDirection === 'ASC' ? 'DESC' : 'ASC';
    setOrderBy(column);
    setOrderDirection(direction);
    setCurrentPage(1);
  };

  const sortedExtrato = useMemo(() => {
    const itemsCopy = [...extrato];
    return itemsCopy.sort((a, b) => {
      let comparison = 0;
      if (orderBy === 'date') {
        comparison = a.timestamp_ordenacao - b.timestamp_ordenacao;
      } else if (orderBy === 'valor') {
        comparison = a.valor - b.valor;
      } else if (orderBy === 'descricao') {
        comparison = a.descricao.localeCompare(b.descricao);
      } else if (orderBy === 'status') {
        comparison = a.status.localeCompare(b.status);
      }
      return orderDirection === 'ASC' ? comparison : -comparison;
    });
  }, [extrato, orderBy, orderDirection]);

  const totalItems = sortedExtrato.length;
  const totalPages = Math.ceil(totalItems / limit) || 1;
  const paginatedExtrato = sortedExtrato.slice((currentPage - 1) * limit, currentPage * limit);

  const exportToCSV = () => {
    if (extrato.length === 0) {
      toast.error(translateInterfaceText("Não há dados para exportar."));
      return;
    }
    const headers = ["Operacao", "Descricao", "Valor", "Moeda", "Status", "Data"];
    const csvRows = sortedExtrato.map(tx => [
      tx.tipo === 'payout' ? 'SAQUE' : translateInterfaceText("COMISSÃO"),
      tx.descricao,
      tx.valor.toFixed(2).replace('.', ','),
      tx.moeda.toUpperCase(),
      tx.status,
      tx.data_solicitacao
    ]);

    const csvString = [headers.join(";"), ...csvRows.map(row => row.join(";"))].join("\n");
    const blob = new Blob(["\ufeff" + csvString], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `extrato_comissoes_${new Date().getTime()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success(translateInterfaceText("Extrato exportado!"));
  };

  if (!user || user.nivel === 'customer') return null;

  const podeSacar = Number(saldoDisponivelCalculado) >= 100.00;

  return (
    <Layout>
      <Header title={translateInterfaceText("Histórico de Saques")} />
      <div className="p-8 max-w-[1600px] mx-auto space-y-6 font-sans antialiased text-slate-800">

        {/* DISTRIBUIÇÃO EM GRID DE DUAS COLUNAS LATERAIS */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">

          {/* COLUNA ESQUERDA: Cards de Resumo de Saldo */}
          <div className="lg:col-span-4 space-y-6">

            {/* Card de Saldo Disponível */}
            <div className="border border-slate-200 shadow-sm bg-white rounded-xl p-6">
              <div className="space-y-1 mb-6">
                <h3 className="text-base font-bold text-black">{translateInterfaceText("Disponível para saque")}</h3>
                <p className="text-xs text-slate-400 font-normal leading-normal">
                  {translateInterfaceText("Este é o saldo liberado baseado nas indicações da sua carteira que você pode transferir imediatamente.")}</p>
              </div>

              <div className="space-y-4">
                <div className="text-3xl font-black tracking-tight text-slate-900">
                  R$ {Number(saldoDisponivelCalculado).toLocaleString(getInterfaceLanguage(), { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>

                <button
                  onClick={() => {
                    if (!podeSacar) {
                      toast.error(translateInterfaceText("Saldo insuficiente para saque (Mínimo R$ 100,00)."));
                      return;
                    }
                    setIsModalOpen(true);
                  }}
                  disabled={payoutLoading}
                  className={`w-full h-11 text-xs font-bold uppercase tracking-wider rounded-xl shadow-sm transition-colors flex items-center justify-center gap-2 ${podeSacar
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-slate-100 text-slate-400 cursor-not-allowed shadow-none'
                    }`}
                >
                  {payoutLoading ? translateInterfaceText("Processando...") : <>{translateInterfaceText("Solicitar Saque Imediato ›")}</>}
                </button>
              </div>
            </div>

            {/* Card de Informações/Regras Importantes */}
            <div className="border border-slate-200 shadow-sm bg-white rounded-xl p-6">
              <div className="space-y-1 mb-4">
                <h3 className="text-base font-bold text-black flex items-center gap-1.5">
                  {translateInterfaceText("Status geral")}</h3>
              </div>
              <div className="space-y-3 text-xs text-slate-500 font-medium">
                <p className="flex items-start gap-1.5">
                  <span className="text-amber-500 font-bold">•</span>
                  {translateInterfaceText("Total solicitado")}{" "}<strong className="text-slate-900 font-bold">R$ {saldoSolicitado}</strong>
                </p>
                <p className="flex items-start gap-1.5">
                  <span className="text-amber-500 font-bold">•</span>
                  {translateInterfaceText("Total geral pago (sem descontos):")}{" "}<strong className="text-slate-900 font-bold">R$ {saldoTotaGeralPago}</strong>
                </p>
              </div>
            </div>

            {/* Card de Informações/Regras Importantes */}
            <div className="border border-slate-200 shadow-sm bg-white rounded-xl p-6">
              <div className="space-y-1 mb-4">
                <h3 className="text-base font-bold text-black flex items-center gap-1.5">
                  {translateInterfaceText("Regras de Operação")}</h3>
              </div>
              <div className="space-y-3 text-xs text-slate-500 font-medium">
                <p className="flex items-start gap-1.5">
                  <span className="text-amber-500 font-bold">•</span>
                  {translateInterfaceText("Saques mínimos autorizados a partir de")}{" "}<strong className="text-slate-900 font-bold">R$ 100,00</strong>
                </p>
                <p className="flex items-start gap-1.5">
                  <span className="text-amber-500 font-bold">•</span>
                  {translateInterfaceText("Prazo mínimo de processamento das solicitações bancárias:")}{" "}<strong className="text-slate-900 font-bold">{translateInterfaceText("3 dias úteis")}</strong>
                </p>
                <p className="flex items-start gap-1.5">
                  <span className="text-amber-500 font-bold">•</span>
                  {translateInterfaceText("Totalizadores gerais no sistema:")}{" "}<strong className="text-slate-900 font-bold">{extrato.length} {" "}{translateInterfaceText("registros")}</strong> {" "}{translateInterfaceText("computados.")}</p>
              </div>
            </div>

            {/* Botão sutil de Exportação */}
            <button
              onClick={exportToCSV}
              className="w-full h-11 text-xs font-bold bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 rounded-xl shadow-sm transition-colors flex items-center justify-center gap-2"
            >
              <Download size={14} /> {" "}{translateInterfaceText("Exportar Extrato CSV")}</button>

          </div>

          {/* COLUNA DIREITA: Tabela de Extrato Tabular Limpa e Organizada */}
          <div className="lg:col-span-8 space-y-4">

            <div className="border border-slate-200 shadow-sm bg-white rounded-xl overflow-hidden">
              <div className="w-full overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="border-b border-slate-100 text-[11px] font-bold tracking-wider text-slate-400 uppercase bg-[#fafafa]">
                      <th className="py-4 px-6 cursor-pointer select-none hover:bg-slate-100/50" onClick={() => handleSort('descricao')}>
                        {translateInterfaceText("Descrição da Transação")}{" "}{orderBy === 'descricao' && (orderDirection === 'ASC' ? '▲' : '▼')}
                      </th>
                      <th className="py-4 px-4 cursor-pointer select-none hover:bg-slate-100/50" onClick={() => handleSort('date')}>
                        {translateInterfaceText("Data")}{" "}{orderBy === 'date' && (orderDirection === 'ASC' ? '▲' : '▼')}
                      </th>
                      <th className="py-4 px-4 cursor-pointer select-none hover:bg-slate-100/50" onClick={() => handleSort('status')}>
                        {translateInterfaceText("Status")}{" "}{orderBy === 'status' && (orderDirection === 'ASC' ? '▲' : '▼')}
                      </th>
                      <th className="py-4 px-4 text-right">{translateInterfaceText("Tipo")}</th>
                      <th className="py-4 px-6 cursor-pointer select-none hover:bg-slate-100/50 text-right" onClick={() => handleSort('valor')}>
                        {translateInterfaceText("Valor")}{" "}{orderBy === 'valor' && (orderDirection === 'ASC' ? '▲' : '▼')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 bg-white">
                    {loading ? (
                      Array.from({ length: 4 }).map((_, i) => (
                        <tr key={i} className="animate-pulse">
                          <td colSpan={5} className="py-5 px-6">
                            <div className="h-4 bg-slate-100 rounded w-full"></div>
                          </td>
                        </tr>
                      ))
                    ) : paginatedExtrato.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="py-12 text-center text-xs text-slate-400 font-medium">
                          {translateInterfaceText("Nenhuma transferência ou comissão localizada para esta conta no BD.")}</td>
                      </tr>
                    ) : (
                      paginatedExtrato.map((tx, index) => {
                        const isPayout = tx.tipo === 'payout';

                        // Configuração visual de tags baseada estritamente nas regras originais
                        let statusColor = "bg-slate-50 text-slate-500 border-slate-100";
                        if (tx.status === 'PAGO' || tx.status === 'CONCLUIDO' || tx.status === 'DISPONIVEL') {
                          statusColor = "bg-emerald-50 text-emerald-600 border-emerald-100";
                        } else if (tx.status === 'PROCESSANDO' || tx.status === 'SOLICITADO') {
                          statusColor = "bg-amber-50 text-amber-600 border-amber-100";
                        } else {
                          statusColor = "bg-red-50 text-red-600 border-red-100";
                        }

                        return (
                          <tr key={`${tx.id_transacao}-${index}`} className="hover:bg-slate-50/50 transition-colors text-xs font-medium text-slate-700">

                            {/* DESCRIÇÃO DA TRANSAÇÃO */}
                            <td className="py-4 px-6 whitespace-nowrap">
                              <div className="flex items-center gap-2.5">
                                <div className={`h-7 w-7 rounded-full text-[10px] font-bold flex items-center justify-center shrink-0 ${isPayout ? 'bg-red-50 text-red-600' : 'bg-emerald-50 text-emerald-600'
                                  }`}>
                                  {isPayout ? <Landmark className="h-3.5 w-3.5" /> : <ArrowUpRight className="h-3.5 w-3.5" />}
                                </div>
                                <span className="font-bold text-slate-900">{tx.descricao}</span>
                              </div>
                            </td>

                            {/* DATA */}
                            <td className="py-4 px-4 text-slate-500 whitespace-nowrap">
                              {tx.data_solicitacao}
                            </td>

                            {/* STATUS */}
                            <td className="py-4 px-4 whitespace-nowrap">
                              <span className={`inline-flex items-center gap-1 border px-2 py-0.5 rounded-full text-[10px] font-bold ${statusColor} ${tx.status === 'PROCESSANDO' ? 'animate-pulse' : ''}`}>
                                <span className="h-1 w-1 rounded-full bg-current" />
                                {tx.status}
                              </span>
                            </td>

                            {/* TIPO + COMPROVANTE */}
                            <td className="py-4 px-4 text-right whitespace-nowrap">
                              <div className="inline-flex flex-col items-end gap-1">
                                {isPayout ? (
                                  <>
                                    <span className="text-red-600 bg-red-50 px-2 py-0.5 rounded text-[10px] font-bold border border-red-100">{translateInterfaceText("Saque")}</span>
                                    {tx.document && (
                                      <button
                                        onClick={() => setProofModalUrl(tx.document!)}
                                        className="text-[10px] text-blue-600 hover:text-blue-700 font-bold uppercase tracking-wider mt-0.5 flex items-center gap-0.5"
                                      >
                                        <Download className="h-2.5 w-2.5" /> {" "}{translateInterfaceText("Comprovante")}</button>
                                    )}
                                  </>
                                ) : (
                                  <span className="text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded text-[10px] font-bold border border-emerald-100">{translateInterfaceText("Comissão")}</span>
                                )}
                              </div>
                            </td>

                            {/* VALOR */}
                            <td className={`py-4 px-6 text-right font-bold font-mono whitespace-nowrap ${isPayout ? 'text-red-600' : 'text-emerald-600'
                              }`}>
                              {isPayout ? '- ' : '+ '}
                              {tx.valor.toLocaleString(getInterfaceLanguage(), { style: 'currency', currency: 'BRL' })}
                            </td>

                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {/* BARRA DE PAGINAÇÃO MODELO CORPORATIVO CLEAN */}
              <div className="flex items-center justify-between px-6 py-4 bg-white border-t border-slate-100 text-xs">
                <span className="text-slate-400 font-medium">
                  {translateInterfaceText("Página")}{" "}<strong className="text-slate-700">{currentPage}</strong> {" "}{translateInterfaceText("de")}{" "}<strong className="text-slate-700">{totalPages}</strong> ({totalItems} {" "}{translateInterfaceText("itens)")}</span>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="h-8 px-3 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 font-bold transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-1"
                  >
                    <ChevronLeft className="h-3.5 w-3.5" /> {" "}{translateInterfaceText("Anterior")}</button>
                  <div className="h-8 w-8 flex items-center justify-center bg-blue-600 rounded-lg text-xs font-bold text-white shadow-sm">
                    {currentPage}
                  </div>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="h-8 px-3 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 font-bold transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-1"
                  >
                    {translateInterfaceText("Próximo")}{" "}<ChevronRight className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>

            </div>
          </div>

        </div>

        {/* MODAL INFORMATIVO DE CONFIRMAÇÃO DE SAQUE */}
        {isModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4 animate-in fade-in duration-200">
            <div className="bg-white rounded-2xl w-full max-w-md border border-slate-200 shadow-2xl overflow-hidden flex flex-col animate-in zoom-in-95 duration-200">

              <div className="p-6 bg-white border-b border-slate-100 flex items-center justify-between">
                <div className="space-y-1 text-left">
                  <h3 className="text-lg font-bold text-slate-900 tracking-tight">{translateInterfaceText("Confirmar Resgate")}</h3>
                  <p className="text-xs text-slate-400 font-normal">{translateInterfaceText("Leia atentamente as condições operacionais de repasse bancário.")}</p>
                </div>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="h-8 w-8 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg flex items-center justify-center transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <div className="p-6 space-y-4 text-slate-600 text-xs font-medium text-left">
                <p className="text-sm">{translateInterfaceText("Você está solicitando o saque do seu saldo liberado de")}{" "}<strong className="text-slate-950 font-black">R$ {saldoDisponivelCalculado}</strong>.</p>

                <div className="p-4 bg-white rounded-xl border border-slate-200 space-y-2">
                  <p className="flex items-start gap-1">{translateInterfaceText("• O valor mínimo regulamentado para saques manuais é de")}{" "}<strong>R$ 100,00</strong>.</p>
                  <p className="flex items-start gap-1">{translateInterfaceText("• O repasse será depositado na conta bancária padrão vinculada ao seu cadastro.")}</p>
                  <p className="flex items-start gap-1">{translateInterfaceText("• Taxas operacionais de saque serão debitadas de acordo com as regras internas do split.")}</p>
                  <p className="text-amber-700 font-bold flex items-start gap-1">{translateInterfaceText("• IMPORTANTE: Após a confirmação, os fundos podem levar até 3 dias úteis para constar em sua conta bancária.")}</p>
                </div>
              </div>

              <div className="px-6 py-4 bg-white border-t border-slate-100 flex justify-end gap-2">
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="h-10 px-4 rounded-xl border border-slate-200 bg-white text-xs font-bold text-slate-600 hover:bg-slate-50 transition-colors"
                >
                  {translateInterfaceText("Voltar")}</button>
                <button
                  onClick={handleRequestPayout}
                  className="h-10 px-5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold transition-colors shadow-none"
                >
                  {translateInterfaceText("Confirmar e Sacar")}</button>
              </div>

            </div>
          </div>
        )}

        {/* MODAL REESTILIZADO PARA COMPROVANTES - IMAGEM 3 */}
        {proofModalUrl && (
          <div className="fixed top-[-5%] inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="relative max-w-lg overflow-hidden bg-white border border-slate-200 rounded-2xl shadow-2xl flex flex-col animate-in zoom-in-95 duration-200">

              {/* Header do Modal conforme Imagem 3 */}
              <div className="p-6 bg-white border-b border-slate-100 flex items-center justify-between">
                <div className="space-y-1 text-left">
                  <h3 className="text-lg font-bold text-slate-900 tracking-tight">{translateInterfaceText("Comprovante de Transferência")}</h3>
                  <p className="text-xs text-slate-400 font-normal">{translateInterfaceText("Documento fiscal oficial emitido pelo processador bancário.")}</p>
                </div>
                <button
                  onClick={() => setProofModalUrl(null)}
                  className="h-8 w-8 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg flex items-center justify-center transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              {/* Corpo da imagem contido (sem vazamentos de tela) */}
              <div className="p-6 bg-slate-50 flex items-center justify-center max-h-[60vh] overflow-y-auto">
                <img
                  src={proofModalUrl}
                  alt={translateInterfaceText("Comprovante de pagamento")}
                  className="max-w-full max-h-[50vh] object-contain rounded-xl border border-slate-200 shadow-sm"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = 'https://placehold.co/400x300?text=Erro+ao+carregar+imagem';
                  }}
                />
              </div>

              {/* Footer do Modal conforme Imagem 3 */}
              <div className="px-6 py-4 bg-white border-t border-slate-100 flex justify-end gap-2">
                <a
                  href={proofModalUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="h-10 px-4 rounded-xl border border-slate-200 bg-white text-xs font-bold text-slate-600 hover:bg-slate-50 transition-colors inline-flex items-center justify-center"
                >
                  {translateInterfaceText("Abrir Original")}</a>
                <button
                  onClick={() => setProofModalUrl(null)}
                  className="h-10 px-5 rounded-xl bg-blue-600 text-white text-xs font-bold hover:bg-blue-700 transition-colors shadow-none"
                >
                  {translateInterfaceText("Fechar Janela")}</button>
              </div>

            </div>
          </div>
        )}

      </div>
    </Layout>
  );
}